import { LenderConfiguration } from '@shared/model/lender-config.model';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { TokenService, OidSessionService } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { Subject, Subscription } from 'rxjs';
import { ApiCacheService } from 'src/postapp/services/api.cache.service';
import { User_api } from 'src/user/constant/user-const';
import { LocationStrategy , isPlatformBrowser } from '@angular/common';
import { StatefulService } from '@app/service/stateful.service';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { StorageWrapperService } from '@workflow/ecp-shared/service/storage-wrapper/storage-wrapper.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { Info, RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { WorkflowLoaderService } from './workflow-loader.service';

/**
 * This is the new user session timeout threshold
 * There is not a need for this to be lender specific
 */
const USER_SESSION_TIMEOUT = 900; // 15 mins in SECONDS.

// const USER_SESSION_TIMEOUT = 145; // for TESTING ONLY

/**
 * service to handle userSession
 *
 * class UserSessionService
 */
@Injectable({
  providedIn: 'root'
})
export class UserSessionService {
  private logger = inject(SawDust);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private logContext(additional: Record<string, unknown> = {}) {
    return { component: 'UserSessionService', ...additional };
  }

  public timerEvent: Subject<string> = new Subject<string>();
  /** Lender data from api */
  public lenderData: LenderConfiguration;
  public subscriptions = new Subscription();
  public canHookCurrentBundle = false;
  public logoutInProgress = false;
  public activityTimerHolder;
  public headerConsts = RequestHeaderConst;
  private tUserFirstName?: string;
  private tUserLastName?: string;

  constructor(
    private readonly api: ApiService,
    private readonly router: Router,
    private readonly storageWrapperService: StorageWrapperService,
    private readonly userService: UserService,
    private readonly apiCacheService: ApiCacheService,
    private readonly locationStrategy: LocationStrategy,
    private readonly sessionService: OidSessionService,
    private readonly statefulService: StatefulService,
    private workflowService: WorkflowService,
    private tokenService: TokenService,
    private readonly workflowLoader: WorkflowLoaderService
  ) {
    this.activityTimerHolder = this.sessionService.inactivityTimer('ecp-user-session').subscribe((timerData) => {
      switch (timerData) {
        case 'warn':
          this.logger.debug('Received inactivity warning from timer', this.logContext());
          this.timerEvent.next(Info.show);
          break;
        case 'logout':
          this.logger.info('Inactivity timer requested logout', this.logContext());
          this.logOut();
          break;
        default:
          this.logger.warn('Unknown message from inactivity timer', this.logContext({ timerData }));
      }
    });
    this.subscriptions.add(this.activityTimerHolder);
  }

  public get token(): string {
    return this.userService.token;
  }

  public userFirstName(): string {
    if (this.tUserFirstName) {
      return this.tUserFirstName;
    }

    if (this.userService.user?.firstName?.length) {
      this.storageWrapperService.set('user.fName', this.userService.user.firstName);
      this.storageWrapperService.set('user.lName', this.userService.user.lastName || '');
      this.tUserFirstName = this.userService.user.firstName;
      this.tUserLastName = this.userService.user.lastName;
    } else {
      this.tUserFirstName = this.storageWrapperService.get('user.fName');
      this.tUserLastName = this.storageWrapperService.get('user.lName');
    }
    return this.tUserFirstName;
  }

  public userFullName(): string {
    return this.userFirstName() + ' ' + this.tUserLastName;
  }

  /**
   * Borrower Activity Timer
   */
  public startTimer(): void {
    this.logger.debug('Starting session activity timer', this.logContext());
    this.sessionService.startActivityTimer(USER_SESSION_TIMEOUT, 'ecp-user-session');
    const token = this.tokenService.getCachedToken();
    if (token) {
      this.tokenService.updateToken(token);
      this.sessionService.startRefreshTimer('ecp-user-session');
    }
  }

  /**
   * Stops the activity timer
   */
  public stopTimer(): void {
    this.logger.debug('Stopping session activity timer', this.logContext());
    this.sessionService.stopActivityTimer('ecp-user-session');
    this.timerEvent.next(Info.hide);
  }

  /**
   * Logs out the user and clears session data.
   *
   * @param navigateToLogin - If true, navigates to the login page after logout.
   * @param userInactive - If true, indicates the user was logged out due to inactivity.
   *
   * This method logs out the user by:
   * - Clearing session data
   * - Stopping timers
   * - Invalidating their token
   * - Navigating to the login page (if needed)
   */
  public logOut(navigateToLogin = true, userInactive = false): void {
    this.logger.info('Initiating user logout', this.logContext({ navigateToLogin, userInactive }));
    this.stopTimer();
    this.statefulService.wrap('ss:authorization').value = '';

    // When a user logouts, theres no need to keep ANYTHING in session Storage
    // Probably add this to a Shared-UI session service
    // TODO: Won't this affect admin portal session storage as well? This seems like it should be surgical.

    this.sessionService.stopActivityTimer('ecp-user-session');

    // Manually adding the authorization header to the logout request
    let auth;
    if (this.isBrowser) {
      const flowType = JSON.parse(sessionStorage.getItem('BMP.FlowType'));
      const storageBlob = JSON.parse(sessionStorage.getItem('cuASSD:ss'));
      if (flowType === 'OLA') {
        auth = storageBlob['auth-ola'];
      } else if (flowType === 'OAO') {
        auth = storageBlob.authorization;
      }
    }
    let authObj = {};
    if (auth) {
      authObj = { defaultHeaders: { [RequestHeaderConst.authorization]: auth, [RequestHeaderConst.dxSessionId]: auth } };
    } else {
      authObj = {};
    }

    // NOTE: Should be refactored to firstValueFrom per https://rxjs.dev/deprecations/to-promise
    this.api
      .request<void>({ urlObj: { ...User_api.LogOut, ...authObj } })
      .toPromise()
      .then(() => {})
      .catch((err) => this.logger.error('Error logging out user', err, this.logContext()))
      .finally(() => {
        this.logoutInProgress = true;
        this.cleanUpUserSession();
        this.navigateOnLogout(navigateToLogin, userInactive);
        this.logger.info('Completed logout workflow', this.logContext({ navigateToLogin, userInactive }));
      });
  }

  /**
   * Cleans up the user session by invalidating the token, logging out the user, clearing session storage,
   * and resetting various services. This method ensures that all user-related data and session information
   * are properly cleared and reset, providing a clean state for the next user session.
   *
   */
  public cleanUpUserSession(): void {
    this.logger.debug('Cleaning up user session artifacts', this.logContext());
    this.tokenService.invalidateToken();
    this.userService.logout();
    if (this.isBrowser) {
      sessionStorage.clear();
    }
    this.apiCacheService.clear();
    this.workflowLoader.clear();
    this.workflowService.clearCurrentFlow();
    this.workflowService.clearAllInstance();
    this.sessionService.destroySession(true, 'ecp-user-session');
    this.statefulService.wrap('ola:extraData').saveLock();
    this.clearUserName(); // Null out usernames (bug #297718)
  }

  /**
   * Clears the stored usernames by setting both `tUserFirstName` and `tUserLastName` to `null`.
   * These values are pulled for things like the Dashboard username (Welcome, {name})
   */
  public clearUserName(): void {
    this.tUserFirstName = this.tUserLastName = null; // Null out usernames (bug #297718)
  }

  // NOTE: Should be refactored to firstValueFrom per https://rxjs.dev/deprecations/to-promis
  public refreshToken(callback?: (data?) => void, errorCallback?: (errorData?) => void): void {
    if (this.userService.token !== undefined) {
      this.logger.debug('Refreshing user token', this.logContext());
      this.api
        .request<void>({ urlObj: User_api.RefreshToken })
        .toPromise()
        .then((data?) => {
          if (callback) {
            callback(data);
          }
        })
        .catch((err) => {
          this.logger.error('Failed to refresh user token', err, this.logContext());
          if (errorCallback) {
            errorCallback(err);
          }
        });
    }
  }

  /**
   * Handles the navigational logic after user a user is programmatically logged out
   *
   * @param navigateToLogin - If true, navigates to the login page.
   * @param userInactive - If true, indicates the user was logged out due to inactivity.
   *
   * This method:
   * - Navigates to the login page (if needed)
   * - Adds a query parameter for inactive users
   * - Prevents back navigation
   */
  public navigateOnLogout(navigateToLogin: boolean, userInactive: boolean): void {
    if (navigateToLogin) {
      if (userInactive) {
        this.logger.info('Navigating to login due to inactivity', this.logContext());
        this.router.navigate(['/user/login'], { queryParams: { inactive: true } });
      } else {
        this.logger.info('Navigating to login after explicit logout', this.logContext());
        this.router.navigate(['/']);
      }
      if (this.isBrowser) {
        history.pushState(null, null);
        this.locationStrategy.onPopState(() => {
          history.pushState(null, null);
        });
      }
    }
  }

  public closeModal(): void {
    this.timerEvent.next(Info.hide);
    this.logger.debug('Dismissed inactivity modal and restarting timer', this.logContext());
    this.startTimer();
  }

  public sessionPollTimer(): void {
    const timeout = 1000;
    setInterval(() => {
      this.sessionPoll();
    }, timeout);
  }

  private sessionPoll(): void {
    let lastSessionToken = this.storageWrapperService.get<string>(Info.lastSessionToken);
    lastSessionToken = lastSessionToken === undefined ? '' : lastSessionToken;
    let currentToken = this.userService.token;
    currentToken = currentToken === undefined ? '' : currentToken;

    if (this.isBrowser && currentToken !== lastSessionToken && currentToken !== '' && lastSessionToken !== '') {
      this.logger.warn('Detected token change in another tab; reloading application', this.logContext());
      location.reload();
    }

    this.storageWrapperService.set(Info.lastSessionToken, currentToken);
  }
}
